import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Button, CircularProgress, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import AttachmentBox from '../../components/AttachmentBox/AttachmentBox';
import DetailsCard from '../../components/DetailsCard/DetailsCard';
import PageTitle from '../../components/PageTitle/PageTitle';
import { dashBorder } from '../../constants';
import { BulkUploadSummary } from '../../models/bulkUploadSummary';
import { bulkPaymentsService } from '../../services/bulkPaymentsService';
import { createDownloadFile } from '../../utils/helpers';
import ImportSummaryModal from './ImportSummaryModal';

const BulkUpload = (): JSX.Element => {
  const fileAttachmentRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorHistoryFiles, setErrorHistoryFiles] = useState<
    { uuid: string; fileName: string }[]
  >([]);
  const [uploadSummary, setUploadSummary] = useState<
    BulkUploadSummary | undefined
  >(undefined);
  const [openImportSummaryModal, setOpenImportSummaryModal] =
    useState<boolean>(false);

  useEffect(() => {
    getErrorHistory();
  }, []);

  const onAttachFile = (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList) {
      setFile(fileList[0]);
    }
    event.target.value = '';
  };

  const onDeleteFile = () => {
    setFile(undefined);
  };

  const onBulkUpload = () => {
    setLoading(true);
    const formData = new FormData();
    formData.set('file', file as Blob);
    bulkPaymentsService
      .uploadFile(formData)
      .then((response) => {
        setLoading(false);
        setUploadSummary(response.data);
        setOpenImportSummaryModal(true);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getErrorHistory = () => {
    bulkPaymentsService.errorHistory().then((response) => {
      setErrorHistoryFiles(response.data);
    });
  };

  const onDeleteErrorFile = (uuid: string) => {
    bulkPaymentsService.deleteError(uuid).then(() => {
      setErrorHistoryFiles((prevState) =>
        prevState.filter((file) => file.uuid !== uuid)
      );
    });
  };

  const exportErrors = (uuid: string, fileName: string) => {
    bulkPaymentsService.exportErrors(uuid).then((response) => {
      createDownloadFile(response.data, fileName);
    });
  };

  return (
    <>
      <PageTitle>Bulk Upload Payments</PageTitle>
      <DetailsCard title="Bulk Uploads">
        <Typography mb={3}>
          Use this page to upload your own Excel document for payments
        </Typography>
        {file && (
          <AttachmentBox
            key={file.name}
            fileName={file.name}
            onClickFile={() => {
              const href = URL.createObjectURL(file);
              window.open(href, 'blank');
            }}
            onDeleteFile={onDeleteFile}
          />
        )}
        <Button
          variant="lightBlue"
          sx={{ my: 3 }}
          onClick={() => {
            fileAttachmentRef.current
              ? fileAttachmentRef.current.click()
              : null;
          }}
        >
          Choose File
        </Button>
        <Box sx={{ display: 'none' }}>
          <input
            type="file"
            title="attachment"
            accept=".xls,.xlsx"
            ref={fileAttachmentRef}
            onChange={(event) => onAttachFile(event)}
          />
        </Box>

        <Typography>Supported File Formats: .xls, .xlsx</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            sx={{ my: 2 }}
            onClick={onBulkUpload}
            disabled={!file || loading}
            startIcon={<CloudUploadIcon />}
          >
            Upload for Payment
          </Button>
          {loading && <CircularProgress size={30} />}
        </Box>
        <Box
          sx={{
            ...dashBorder,
            my: 4
          }}
        >
          <Typography variant="h5" mb={3}>
            Error History
          </Typography>
          {errorHistoryFiles.map((file) => (
            <AttachmentBox
              key={file.uuid}
              fileName={file.fileName}
              onDeleteFile={() => onDeleteErrorFile(file.uuid)}
              onClickFile={() => exportErrors(file.uuid, file.fileName)}
            />
          ))}
          {!errorHistoryFiles.length && (
            <Typography>No error files available for download.</Typography>
          )}
        </Box>
      </DetailsCard>
      <ImportSummaryModal
        openModal={openImportSummaryModal}
        closeModal={() => setOpenImportSummaryModal(false)}
        onSuccess={getErrorHistory}
        paymentSummary={uploadSummary}
      />
    </>
  );
};

export default BulkUpload;
